import 'reflect-metadata'
import { BrowserModule } from '@angular/platform-browser'
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core'
import { registerLocaleData } from '@angular/common'
import localeCs from '@angular/common/locales/cs'
import { SnotifyModule, SnotifyService, ToastDefaults } from 'ng-alt-snotify'
import { PhotoGalleryModule } from '@twogate/ngx-photo-gallery'

import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { AccordionModule } from 'ngx-bootstrap/accordion'
import { AlertModule } from 'ngx-bootstrap/alert'
import { ButtonsModule } from 'ngx-bootstrap/buttons'
import { BsDropdownModule } from 'ngx-bootstrap/dropdown'
import { ModalModule } from 'ngx-bootstrap/modal'
import { ProgressbarModule } from 'ngx-bootstrap/progressbar'

import { NgSelectModule } from '@ng-select/ng-select'

import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { ServiceWorkerModule } from '@angular/service-worker'
import { environment } from '../environments/environment'

import { AppLoaderComponent } from './components/_common/app-loader/app-loader.component'
import { BaseListingComponent } from './components/_common/base-listing/base-listing.component'
import { BtnComponent } from './components/_common/btn/btn.component'
import { DropdownComponent } from './components/_common/dropdown/dropdown.component'
import { FormRowCheckboxComponent } from './components/_common/form-row-checkbox/form-row-checkbox.component'
import { FormRowInputComponent } from './components/_common/form-row-input/form-row-input.component'
import { FormRowPasswordComponent } from './components/_common/form-row-password/form-row-password.component'
import { FormRowSelectComponent } from './components/_common/form-row-select/form-row-select.component'
import { FormRowSelectMultipleComponent } from './components/_common/form-row-select-multiple/form-row-select-multiple.component'
import { FormRowTextareaComponent } from './components/_common/form-row-textarea/form-row-textarea.component'
import { FormRowToggleComponent } from './components/_common/form-row-toggle/form-row-toggle.component'
import { InfoTooltipComponent } from './components/_common/info-tooltip/info-tooltip.component'

import { SideMenuComponent } from './components/side-menu/side-menu.component'
import { HeaderComponent } from './components/header/header.component'
import { ModalAddressCreateComponent } from './components/modal-components/modal-address-create/modal-address-create.component'
import { ModalChangePassComponent } from './components/modal-components/modal-change-pass/modal-change-pass.component'
import { ModalExportCreateComponent } from './components/modal-components/modal-export-create/modal-export-create.component'
import { ModalPrintExportComponent } from './components/modal-components/modal-print-export/modal-print-export.component'
import { ModalProductParentComponent } from './components/modal-components/modal-product-parent/modal-product-parent.component'
import { ModalProductSearchStatsComponent } from './components/modal-components/modal-product-search-stats/modal-product-search-stats'
import { ModalQrCodeComponent } from './components/modal-components/modal-qrcode/modal-qrcode.component'
import { ModalStatusComponent } from './components/modal-components/modal-status/modal-status.component'
import { ModalStockLoadComponent } from './components/modal-components/modal-stock-load/modal-stock-load.component'
import { ModalStockOperationCardComponent } from './components/modal-components/modal-stock-operation-card/modal-stock-operation-card.component'
import { ModalStockOverviewComponent } from './components/modal-components/modal-stock-overview/modal-stock-overview.component'
import { ModalStockPositionsComponent } from './components/modal-components/modal-stock-positions/modal-stock-positions.component'
import { ProductMovesListComponent } from './components/product-components/product-moves-list/product-moves-list.component'
import { BoxUserComponent } from './components/box-user/box-user.component'

import { CheckoutComponent } from './pages/checkout/checkout.component'
import { CheckoutsComponent } from './pages/checkouts/checkouts.component'
import { CompanySettingsComponent } from './pages/company-settings/company-settings.component'
import { DepotComponent } from './pages/depot/depot.component'
import { DepotsComponent } from './pages/depots/depots.component'
import { ExportsComponent } from './pages/exports/exports.component'
import { HomeComponent } from './pages/home/home.component'
import { LoginComponent } from './pages/login/login.component'
import { NotFoundComponent } from './pages/not-found/not-found.component'
import { ProductPurchaseOrderCreationComponent } from './pages/product-purchase-order-creation/product-purchase-order-creation.component'
import { ModalProductSearchPurchaseComponent } from './pages/product-purchase-order-creation/components/modal-product-search-purchase/modal-product-search-purchase.component'
import { ProductPurchaseOrderCreationHsComponent } from './pages/product-purchase-order-creation-hs/product-purchase-order-creation-hs.component'
import { ModalProductSearchPurchaseHsComponent } from './pages/product-purchase-order-creation-hs/components/modal-product-search-purchase-hs/modal-product-search-purchase-hs.component'
import { ProductPurchaseOrderForkComponent } from './pages/product-purchase-order-fork/product-purchase-order-fork.component'
import { PurchaseOrdersComponent } from './pages/purchase-orders/purchase-orders.component'
import { PurchaseOrderCheckoutComponent } from './pages/purchase-order-checkout/purchase-order-checkout.component'
import { PurchaseOrderItemsComponent } from './pages/purchase-order-items/purchase-order-items.component'
import { ProductComponent } from './pages/product/product.component'
import { ProductMovePackComponent } from './pages/product-move-pack/product-move-pack.component'
import { ProductMovePacksComponent } from './pages/product-move-packs/product-move-packs.component'
import { ProductsComponent } from './pages/products/products.component'
import { StockOperationListComponent } from './pages/stock-operation-list/stock-operation-list.component'
import { StatsComponent } from './pages/stats/stats.component'
import { SupplierComponent } from './pages/supplier/supplier.component'
import { SuppliersComponent } from './pages/suppliers/suppliers.component'
import { UserComponent } from './pages/user/user.component'
import { UsersComponent } from './pages/users/users.component'

import { AuthGuardAdminService, AuthGuardService, NotificationService, TrialService } from './services'

import {
  ColumnSettingsOptionsPipe,
  FmtDatePipe,
  FormatCurrencyWithoutPricePipe,
  FormatPricePipe,
  FormErrorPipe,
  ListCustomColumnPipe,
  ListLengthPipe,
  Nl2brPipe,
  OperationsPriceSumPipe,
  ShortenStringPipe,
  StrToDatePipe,
} from './pipes'

import { ReplaceCommaDirective, TranslateBarcodeDirective } from './directives'

import { NgxDropzoneModule } from 'ngx-dropzone'
import { CKEditorModule } from '@ckeditor/ckeditor5-angular'

import { CategoriesComponent } from './pages/categories/categories.component'
import { CategoryComponent } from './pages/category/category.component'
import { CategoryTreeRowComponent } from './components/category-tree-row/category-tree-row.component'
import { CategoryTreeComponent } from './components/category-tree/category-tree.component'
import { ChartComponent } from './components/chart/chart.component'
import { ModalContainerComponent } from './components/_common/modal-container/modal-container.component'
import { PageHeaderComponent } from './components/_common/page-header/page-header.component'
import { ProductImageComponent } from './components/_common/product-image/product-image.component'
import { FilterFormComponent } from './components/_common/filter-form/filter-form.component'
import { DndModule } from 'ngx-drag-drop'
import { TabsModule } from 'ngx-bootstrap/tabs'
import { FormRowNumberWithControlsComponent } from './components/_common/form-row-number-with-controls/form-row-number-with-controls.component'
import { LoadingSpinnerComponent } from './components/_common/loading-spinner/loading-spinner.component'
import { ProductFormComponent } from './components/product-components/product-form/product-form.component'
import { ProductAutomaticOrderingComponent } from './components/product-components/product-automatic-ordering/product-automatic-ordering.component'
import { ProductCategoriesComponent } from './components/product-components/product-categories/product-categories.component'
import { ProductDepotsFormComponent } from './components/product-components/product-depots-form/product-depots-form.component'
import { ProductImagesComponent } from './components/product-components/product-images/product-images.component'
import { ProductFilesComponent } from './components/product-components/product-files/product-files.component'
import { PopoverModule } from 'ngx-bootstrap/popover'
import { PaginationBtnComponent } from './components/pagination-btn/pagination-btn.component'
import { ModalBulkEditsComponent } from './components/modal-components/bulk-edits/modal-bulk-edits.component'
import { ModalProductDepotMetricComponent } from './components/modal-components/modal-product-depot-metric/modal-product-depot-metric.component'
import { ModalProductHsSupplierConfirmComponent } from './components/modal-components/modal-product-hs-supplier-confirm/modal-product-hs-supplier-confirm.component'
import { ModalProductHsSyncComponent } from './components/modal-components/modal-product-hs-sync/modal-product-hs-sync.component'
import { ModalCategorySearchComponent } from './components/modal-components/modal-category-search/modal-category-search.component'
import { ProductTemplateSelectionComponent } from './components/product-components/product-template-selection/product-template-selection.component'
import { InstitutionsComponent } from './pages/institutions/institutions.component'
import { ModalProductsImportComponent } from './components/modal-components/modal-products-import/modal-products-import.component'

import { AutomaticOrdersComponent } from './pages/automatic-orders/automatic-orders.component'
import { InstitutionComponent } from './pages/institution/institution.component'
import { PrintInventoryExportsComponent } from './pages/print-inventory-exports/print-inventory-exports.component'
import { PrintInventoryDetailComponent } from './pages/print-inventory-detail/print-inventory-detail.component'
import { StockTransferPageComponent } from './pages/stock-transfer-page/stock-transfer-page.component'
import { TooltipModule } from 'ngx-bootstrap/tooltip'
import { ProductPackingComponent } from './components/product-components/product-packing/product-packing.component'
import * as Sentry from '@sentry/angular-ivy'
import { Router, TitleStrategy } from '@angular/router'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { CustomPageTitleStrategy } from './services/custom-page-title-strategy.service'
import { WizardComponent } from './pages/plug-play/pages/wizard/wizard.component'
import { Step1Component } from './pages/plug-play/pages/step1/step1.component'
import { Step2Component } from './pages/plug-play/pages/step2/step2.component'
import { MenuItemComponent } from './pages/plug-play/components/menu-item/menu-item.component'
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog'
import { CloseComponent } from './pages/plug-play/dialogs/close/close.component'
import { Step3Component } from './pages/plug-play/pages/step3/step3.component'
import { Step4Component } from './pages/plug-play/pages/step4/step4.component'
import { Step5Component } from './pages/plug-play/pages/step5/step5.component'
import { Step6Component } from './pages/plug-play/pages/step6/step6.component'
import { Step7Component } from './pages/plug-play/pages/step7/step7.component'
import { Step8Component } from './pages/plug-play/pages/step8/step8.component'
import { Step3EditNewUserComponent } from './pages/plug-play/pages/step3-edit-new-user/step3-edit-new-user.component'
import { UserGroupsPipe } from './pages/plug-play/pages/step3/user-groups.pipe'
import { InvalidListComponent } from './pages/plug-play/dialogs/invalid-list/invalid-list.component'
import { NgxsEmitPluginModule } from '@ngxs-labs/emitter'
import { NgxsModule } from '@ngxs/store'
import { PlugplayState } from './pages/plug-play/states/plugplay-state.service'
import { ProgressBarModule } from 'primeng/progressbar'
import { Step7ImportComponent } from './pages/plug-play/pages/step7-import/step7-import.component'
import { Step7ResultComponent } from './pages/plug-play/pages/step7-result/step7-result.component'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin'
import { DeletionComponent } from './pages/plug-play/dialogs/deletion/deletion.component'
import { ScrollService } from './services/scroll.service'
import { GoogleTagManagerModule } from 'angular-google-tag-manager'
import { GlobalErrorHandler } from './services/global-error.handler'

registerLocaleData(localeCs)

@NgModule({
  declarations: [
    AppComponent,
    // common, atomical components
    AppLoaderComponent,
    BaseListingComponent,
    BtnComponent,
    DropdownComponent,
    FormRowCheckboxComponent,
    FormRowInputComponent,
    FormRowPasswordComponent,
    FormRowSelectComponent,
    FormRowSelectMultipleComponent,
    FormRowTextareaComponent,
    FormRowToggleComponent,
    InfoTooltipComponent,
    ModalContainerComponent,
    // part view components
    BoxUserComponent,
    CategoriesComponent,
    CategoryComponent,
    CategoryTreeRowComponent,
    CategoryTreeComponent,
    ChartComponent,
    CheckoutComponent,
    CheckoutsComponent,
    CompanySettingsComponent,
    DepotComponent,
    DepotsComponent,
    ExportsComponent,
    LoginComponent,
    NotFoundComponent,
    HeaderComponent,
    HomeComponent,
    ModalAddressCreateComponent,
    ModalChangePassComponent,
    ModalExportCreateComponent,
    ModalPrintExportComponent,
    ModalStockLoadComponent,
    ModalStockOperationCardComponent,
    ModalStockOverviewComponent,
    ModalStockPositionsComponent,
    ModalProductParentComponent,
    ModalProductSearchStatsComponent,
    ModalQrCodeComponent,
    ModalStatusComponent,
    ProductPurchaseOrderCreationComponent,
    ModalProductSearchPurchaseComponent,
    ProductPurchaseOrderCreationHsComponent,
    ModalProductSearchPurchaseHsComponent,
    ProductPurchaseOrderForkComponent,
    PurchaseOrdersComponent,
    PurchaseOrderCheckoutComponent,
    PurchaseOrderItemsComponent,
    ProductComponent,
    ProductMovePackComponent,
    ProductMovePacksComponent,
    ProductMovesListComponent,
    ProductsComponent,
    SideMenuComponent,
    StockOperationListComponent,
    StatsComponent,
    SupplierComponent,
    SuppliersComponent,
    UserComponent,
    UsersComponent,
    WizardComponent,
    Step1Component,
    Step2Component,
    MenuItemComponent,
    // pipes
    ColumnSettingsOptionsPipe,
    FmtDatePipe,
    FormatPricePipe,
    FormatCurrencyWithoutPricePipe,
    FormErrorPipe,
    Nl2brPipe,
    OperationsPriceSumPipe,
    ListCustomColumnPipe,
    ListLengthPipe,
    ShortenStringPipe,
    StrToDatePipe,
    // directives
    PageHeaderComponent,
    ProductImageComponent,
    ReplaceCommaDirective,
    TranslateBarcodeDirective,
    FilterFormComponent,
    FormRowNumberWithControlsComponent,
    LoadingSpinnerComponent,
    ProductFormComponent,
    ProductAutomaticOrderingComponent,
    ProductCategoriesComponent,
    ProductDepotsFormComponent,
    ProductImagesComponent,
    ProductFilesComponent,
    PaginationBtnComponent,
    ModalBulkEditsComponent,
    ModalProductDepotMetricComponent,
    ModalProductHsSupplierConfirmComponent,
    ModalProductHsSyncComponent,
    ModalCategorySearchComponent,
    ProductTemplateSelectionComponent,
    InstitutionsComponent,
    ModalProductsImportComponent,
    AutomaticOrdersComponent,
    InstitutionComponent,
    PrintInventoryExportsComponent,
    PrintInventoryDetailComponent,
    StockTransferPageComponent,
    ProductPackingComponent,
    CloseComponent,
    Step3Component,
    Step4Component,
    Step5Component,
    Step6Component,
    Step7Component,
    Step8Component,
    Step3EditNewUserComponent,
    UserGroupsPipe,
    InvalidListComponent,
    Step7ImportComponent,
    Step7ResultComponent,
    DeletionComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    // angular modules
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    BrowserAnimationsModule,
    SnotifyModule.forRoot(),
    PhotoGalleryModule.forRoot({
      defaultOptions: {
        arrowEl: true,
        closeEl: true,
        captionEl: true,
        fullscreenEl: true,
        zoomEl: true,
        shareEl: true,
        counterEl: true,
        preloaderEl: true,
        indexIndicatorSep: ' / ',
      },
    }),
    CKEditorModule,
    // ngx bootstrap/pagination modules
    AccordionModule.forRoot(),
    AlertModule.forRoot(),
    ButtonsModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    NgxDropzoneModule,
    ProgressbarModule.forRoot(),
    TabsModule.forRoot(),
    DndModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    PopoverModule,
    TooltipModule,
    TranslateModule.forRoot({
      defaultLanguage: 'cs',
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new TranslateHttpLoader(http),
        deps: [HttpClient],
      },
    }),
    DynamicDialogModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    NgxsModule.forRoot([PlugplayState], { developmentMode: false }),
    NgxsEmitPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({
      storage: StorageOption.SessionStorage,
      key: ['plugplay'],
    }),
    GoogleTagManagerModule.forRoot({
      id: 'GTM-MGC6TV6S',
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    { provide: 'SnotifyToastConfig', useValue: ToastDefaults },
    { provide: TitleStrategy, useClass: CustomPageTitleStrategy },
    { provide: 'googleTagManagerId', useValue: 'GTM-MGC6TV6S' },
    SnotifyService,
    AuthGuardService,
    AuthGuardAdminService,
    NotificationService,
    DialogService,
    ScrollService,
    TrialService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
